import { ApiError } from "@/lib/api/error";
import { CircuitOpenError, OfflineError } from "@/lib/api/circuitBreaker";

export type AppErrorCategory =
  | "network-offline"
  | "server-unavailable"
  | "unknown";

export interface ClassifiedAppError {
  readonly category: AppErrorCategory;
  readonly code: string;
}

export function classifyAppError(error: unknown): ClassifiedAppError {
  if (isOfflineAppError(error)) return buildClassifiedError("network-offline");
  if (isServerUnavailableError(error)) {
    return buildClassifiedError("server-unavailable");
  }
  return buildClassifiedError("unknown", stableDigest(error));
}

export function isChunkStaleError(error: unknown): boolean {
  const message = errorMessage(error);
  if (matchesChunkStale(message)) return true;
  if (isErrorWithCause(error)) return isChunkStaleError(error.cause);
  return false;
}

function matchesChunkStale(message: string): boolean {
  return /ChunkLoadError|Loading chunk|dynamically imported module/i.test(
    message,
  );
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isErrorWithCause(
  error: unknown,
): error is { readonly cause: unknown } {
  return typeof error === "object" && error !== null && "cause" in error;
}

export function stableDigest(error: unknown): string {
  const source = error instanceof Error ? error.name : typeof error;
  let hash = 2166136261;
  for (const char of source)
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return (hash >>> 0).toString(16).padStart(8, "0").slice(0, 8);
}

function buildClassifiedError(
  category: AppErrorCategory,
  digest = stableDigest(category),
): ClassifiedAppError {
  return { category, code: `QB-${codePart(category)}-${digest}` };
}

function codePart(category: AppErrorCategory): string {
  if (category === "network-offline") return "NET";
  if (category === "server-unavailable") return "SRV";
  return "UNK";
}

function isOfflineAppError(error: unknown): boolean {
  if (error instanceof OfflineError) return true;
  return (
    error instanceof TypeError &&
    typeof navigator !== "undefined" &&
    navigator.onLine === false
  );
}

function isServerUnavailableError(error: unknown): boolean {
  if (error instanceof CircuitOpenError) return true;
  return error instanceof ApiError && error.status >= 500;
}

const STALE_CHUNK_RELOAD_KEY = "qb:stale-chunk-reload-at";
const STALE_CHUNK_RELOAD_COOLDOWN_MS = 10_000;

/** Recover from a STALE lazy chunk after a deploy. The old hashed chunk file is
 *  404 on the server (a new build replaced it), so retrying the same dynamic
 *  import NEVER succeeds — only a page reload (fresh index.html → new chunk
 *  hashes) fixes it. The operator hit this opening a trade: the lazy
 *  `TradeCardMiniChart` chunk 404'd and the screen froze/blanked because the
 *  error boundary just re-rendered the same failing tree.
 *
 *  Reloads AT MOST once per cooldown (sessionStorage timestamp) so a genuinely
 *  broken chunk — not merely stale — can't reload-loop forever; a second
 *  chunk-stale error inside the cooldown falls through to the boundary fallback.
 *  Returns true when it triggered a reload (caller skips its normal handling).
 *  `now`/`storage`/`reload` are injectable for tests. */
export function reloadForStaleChunkOnce(
  error: unknown,
  now: number = Date.now(),
  storage: Storage | null = safeSessionStorage(),
  reload: () => void = defaultReload,
): boolean {
  if (!isChunkStaleError(error)) return false;
  if (storage === null) return false;
  // Number(null) === 0 (no prior reload) and Number("1000") === 1000, so no
  // string fallback is needed — the `last > 0` guard handles the absent case.
  const last = Number(storage.getItem(STALE_CHUNK_RELOAD_KEY));
  if (
    Number.isFinite(last) &&
    last > 0 &&
    now - last < STALE_CHUNK_RELOAD_COOLDOWN_MS
  ) {
    return false;
  }
  storage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
  reload();
  return true;
}

function safeSessionStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    // Private browsing / blocked storage — never throw from the error path.
    return null;
  }
}

function defaultReload(): void {
  if (typeof window !== "undefined") window.location.reload();
}
